import { createZodDto } from 'nestjs-zod/dto';
import { z } from 'zod';
import { encryptedKeyByRsaKeyValidator, stringValidator } from '#validation';
import { filespaceFormatValidator, filespaceNameValidator, storageAccessKeyIdValidator, storageBucketValidator, storageCredentialsTypeValidator, storageEndpointValidator, storageOwnerValidator, storageProviderIdValidator, storageRegionNameValidator, storageSecretAccessKeyValidator, subscriptionTypeValidator, } from '#validation/filespace';
import { FilespaceVersion, mapFilespaceFormatToVersion, STORAGE_PROVIDER_OTHER, StorageCredentialsType, StorageOwner, } from '#validation/filespace/constants';
// The Lost&Found rescue tree exists exactly on V13+ filespaces; the creating client builds its
// crypto material (it alone holds the root folder keys) and the hub-side initializer commits it.
const LOST_AND_FOUND_MIN_FILESPACE_VERSION = FilespaceVersion.V13;
export const initCryptoParamsSchema = z.object({
    encryptedRootFolderAccess: stringValidator({
        requiredError: 'filespace.initCryptoParams.encryptedRootFolderAccess.required',
    }),
    encryptedCommonConfigKey: stringValidator({
        requiredError: 'filespace.initCryptoParams.encryptedCommonConfigKey.required',
    }),
    unencryptedCommonConfigKey: stringValidator({
        requiredError: 'filespace.initCryptoParams.unencryptedCommonConfigKey.required',
    }),
    // Lost&Found rescue-tree material (V13+ only): per rescue directory the name HMAC and the
    // encrypted FWD row value, the client-encrypted tree crypto key, and the filespace admin
    // PUBLIC key (plain PEM) the rescue seals wrap their admin copies to
    lostAndFound: z
        .object({
        dirNameHmac: z.string().min(1),
        dirFwdValue: z.string().min(1),
        subdirNameHmac: z.string().min(1),
        subdirFwdValue: z.string().min(1),
        encryptedCryptoKey: z.string().min(1),
        filespaceAdminPublicKey: z.string().min(1),
    })
        .optional(),
});
export const filespaceCreateSchema = z
    .object({
    name: filespaceNameValidator,
    eFilespaceRootPrivateKeyByAdminKey: encryptedKeyByRsaKeyValidator,
    filespaceFormat: filespaceFormatValidator,
    subscriptionType: subscriptionTypeValidator,
    storageOwner: storageOwnerValidator,
    storageProviderId: storageProviderIdValidator,
    storageRegionId: z.string().optional(),
    storageRegionName: storageRegionNameValidator.optional(),
    storageEndpoint: storageEndpointValidator.optional(),
    storageBucket: storageBucketValidator.optional(),
    storageAccessKeyId: storageAccessKeyIdValidator.optional(),
    storageSecretAccessKey: storageSecretAccessKeyValidator.optional(),
    storageCredentialsType: storageCredentialsTypeValidator.default(StorageCredentialsType.ACCESS_KEY),
    storageRoleArn: z.string().optional(),
    storageRoleExternalId: z.string().optional(),
    storageBlockSize: z.int().positive().optional(),
    initCryptoParams: initCryptoParamsSchema,
})
    // The Lost&Found material must be present exactly for V13+ filespaces: without it a V13+
    // filespace would be initialized without its rescue tree, and below V13 nothing consumes it
    .refine(({ filespaceFormat, initCryptoParams }) => {
    const isLostAndFoundRequired = mapFilespaceFormatToVersion(filespaceFormat) >=
        LOST_AND_FOUND_MIN_FILESPACE_VERSION;
    return (Boolean(initCryptoParams.lostAndFound) ===
        isLostAndFoundRequired);
}, {
    path: ['initCryptoParams'],
    error: 'filespace.initCryptoParams.lostAndFound.formatMismatch',
})
    .refine(({ storageProviderId, storageRegionName }) => {
    if (storageProviderId === STORAGE_PROVIDER_OTHER) {
        return Boolean(storageRegionName);
    }
    return true;
}, {
    path: ['storageRegionName'],
    error: 'filespace.storageRegionName.required',
})
    // Validate region is provided if lucid storage is selected
    .refine(({ storageOwner, storageRegionId }) => {
    if (storageOwner === StorageOwner.LUCIDLINK) {
        return Boolean(storageRegionId);
    }
    return true;
}, {
    path: ['storageRegionId'],
    error: 'filespace.storageRegionId.required',
})
    // Validate storageEndpoint is provided in case we don't have have regionId
    .refine(({ storageOwner, storageRegionId, storageEndpoint }) => {
    if (storageOwner === StorageOwner.CUSTOMER && !storageRegionId) {
        return Boolean(storageEndpoint);
    }
    return true;
}, {
    path: ['storageEndpoint'],
    error: 'filespace.storageEndpoint.required',
})
    // Access-key credentials (the default)
    .refine(({ storageOwner, storageCredentialsType, storageAccessKeyId }) => {
    if (storageOwner === StorageOwner.CUSTOMER &&
        storageCredentialsType === StorageCredentialsType.ACCESS_KEY) {
        return storageAccessKeyId !== undefined;
    }
    return true;
}, {
    path: ['storageAccessKeyId'],
    error: 'filespace.storageAccessKeyId.required',
})
    .refine(({ storageOwner, storageCredentialsType, storageSecretAccessKey }) => {
    if (storageOwner === StorageOwner.CUSTOMER &&
        storageCredentialsType === StorageCredentialsType.ACCESS_KEY) {
        return storageSecretAccessKey !== undefined;
    }
    return true;
}, {
    path: ['storageSecretAccessKey'],
    error: 'filespace.storageSecretAccessKey.required',
})
    // Role credentials
    .refine(({ storageOwner, storageCredentialsType, storageRoleArn }) => {
    if (storageOwner === StorageOwner.CUSTOMER &&
        storageCredentialsType === StorageCredentialsType.ROLE) {
        return storageRoleArn !== undefined;
    }
    return true;
}, {
    path: ['storageRoleArn'],
    error: 'filespace.storageRoleArn.required',
})
    .refine(({ storageOwner, storageCredentialsType, storageRoleExternalId }) => {
    if (storageOwner === StorageOwner.CUSTOMER &&
        storageCredentialsType === StorageCredentialsType.ROLE) {
        return storageRoleExternalId !== undefined;
    }
    return true;
}, {
    path: ['storageRoleExternalId'],
    error: 'filespace.storageRoleExternalId.required',
});
export class FilespaceCreateDto extends createZodDto(filespaceCreateSchema) {
}
